import { useState } from "react";
import { CheckCircle2, AlertTriangle, HelpCircle, ImageIcon, Layers, Table2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

/** Renders the placeholder compatibility report returned by
 *  ``POST /customers/{id}/quote-templates`` (upload) or
 *  ``GET  /customers/{id}/quote-templates/{tid}/analyze`` (re-check).
 *
 *  Props:
 *    - open, onClose
 *    - templateName: string (shown in the dialog title)
 *    - analysis: { total_placeholders, photo_placeholder_count, sheets[], known[], typos[], unknown[] }
 *    - onAutofix?: async () => void — when provided, an "一键修复" button appears
 *      whenever typos > 0. The caller runs the autofix API call and updates the
 *      `analysis` prop; this component just triggers it and shows a loading state.
 */
export default function TemplateAnalysisDialog({ open, onClose, templateName, analysis, onAutofix }) {
  const { t } = useLanguage();
  const [fixing, setFixing] = useState(false);
  if (!analysis) return null;
  const knownCount = (analysis.known || []).length;
  const typoCount = (analysis.typos || []).length;
  const unknownCount = (analysis.unknown || []).length;
  const clean = typoCount === 0 && unknownCount === 0;

  const handleAutofix = async () => {
    if (!onAutofix || typoCount === 0) return;
    if (!window.confirm(t("templateAnalysis.autofixConfirm").replace("{count}", typoCount))) return;
    setFixing(true);
    try {
      await onAutofix();
    } finally {
      setFixing(false);
    }
  };

  const badge = (icon, count, tone, label) => (
    <div
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${tone}`}
      data-testid={`tpl-analysis-badge-${label}`}
    >
      {icon}
      <span>{count}</span>
      <span className="text-[10px] uppercase tracking-wide opacity-70">{label}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="template-analysis-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {clean ? (
              <CheckCircle2 className="text-emerald-500" size={20} />
            ) : (
              <AlertTriangle className="text-amber-500" size={20} />
            )}
            {t("templateAnalysis.title")}
            <span className="text-sm font-normal text-muted-foreground">· {templateName}</span>
          </DialogTitle>
          <DialogDescription>
            {clean ? t("templateAnalysis.allClean") : t("templateAnalysis.issuesFound")}
          </DialogDescription>
        </DialogHeader>

        {/* Summary badges */}
        <div className="flex flex-wrap gap-2">
          {badge(<CheckCircle2 size={12} />, knownCount, "border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300", t("templateAnalysis.known"))}
          {badge(<AlertTriangle size={12} />, typoCount, typoCount > 0 ? "border-amber-500/40 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300" : "border-border bg-muted/40 text-muted-foreground", t("templateAnalysis.typos"))}
          {badge(<HelpCircle size={12} />, unknownCount, unknownCount > 0 ? "border-rose-500/40 bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300" : "border-border bg-muted/40 text-muted-foreground", t("templateAnalysis.unknown"))}
          {badge(<ImageIcon size={12} />, analysis.photo_placeholder_count || 0, "border-border bg-muted/40 text-muted-foreground", t("templateAnalysis.photos"))}
          {badge(<Layers size={12} />, (analysis.sheets || []).length, "border-border bg-muted/40 text-muted-foreground", t("templateAnalysis.sheets"))}
        </div>

        {/* Typos */}
        {typoCount > 0 && (
          <section className="space-y-1.5" data-testid="tpl-analysis-typos">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
              <AlertTriangle size={14} /> {t("templateAnalysis.typosHeader")}
            </h3>
            <p className="text-[11px] text-muted-foreground">{t("templateAnalysis.typosHint")}</p>
            <div className="rounded-md border border-amber-500/20 bg-amber-50/40 dark:bg-amber-950/10 divide-y divide-amber-500/20">
              {analysis.typos.map((typo, i) => (
                <div key={i} className="px-3 py-2 text-xs flex items-center flex-wrap gap-x-3 gap-y-1"
                  data-testid={`tpl-analysis-typo-row-${i}`}>
                  <span className="font-mono text-rose-600 dark:text-rose-400">{`{{${typo.token}}}`}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-mono text-emerald-700 dark:text-emerald-400">{`{{${typo.suggestion}}}`}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                    {typo.sheet} · {typo.coord}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Unknown */}
        {unknownCount > 0 && (
          <section className="space-y-1.5" data-testid="tpl-analysis-unknown">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 text-rose-700 dark:text-rose-300">
              <HelpCircle size={14} /> {t("templateAnalysis.unknownHeader")}
            </h3>
            <p className="text-[11px] text-muted-foreground">{t("templateAnalysis.unknownHint")}</p>
            <div className="rounded-md border border-rose-500/20 bg-rose-50/40 dark:bg-rose-950/10 divide-y divide-rose-500/20">
              {analysis.unknown.map((u, i) => (
                <div key={i} className="px-3 py-2 text-xs flex items-center flex-wrap gap-x-3"
                  data-testid={`tpl-analysis-unknown-row-${i}`}>
                  <span className="font-mono text-rose-600 dark:text-rose-400">{`{{${u.token}}}`}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                    {u.sheet} · {u.coord}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sheets breakdown */}
        <section className="space-y-1.5" data-testid="tpl-analysis-sheets">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <Table2 size={14} /> {t("templateAnalysis.sheetsHeader")}
          </h3>
          <div className="rounded-md border border-border bg-secondary/20 divide-y divide-border">
            {(analysis.sheets || []).map((s, i) => (
              <div key={i} className="px-3 py-2 text-xs flex items-center flex-wrap gap-x-3 gap-y-1"
                data-testid={`tpl-analysis-sheet-row-${i}`}>
                <span className="font-medium">{s.name}</span>
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${s.layout_mode === "table" ? "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300" : "bg-muted text-muted-foreground"}`}>
                  {s.layout_mode}
                </span>
                <span className="text-muted-foreground">{s.placeholder_count} {t("templateAnalysis.placeholders")}</span>
                {s.photo_placeholder_count > 0 && (
                  <span className="text-muted-foreground">· {s.photo_placeholder_count} {t("templateAnalysis.photos")}</span>
                )}
                {s.row_placeholder_rows?.length > 0 && (
                  <span className="ml-auto text-[10px] font-mono text-muted-foreground">
                    row.* @ {s.row_placeholder_rows.join(", ")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        <DialogFooter>
          {onAutofix && typoCount > 0 && (
            <Button
              onClick={handleAutofix}
              disabled={fixing}
              variant="outline"
              data-testid="tpl-analysis-autofix-button"
              className="rounded-md gap-1.5 border-amber-500/60 text-amber-700 hover:bg-amber-50 dark:text-amber-300"
            >
              <Wand2 size={14} />
              {fixing ? "…" : t("templateAnalysis.autofix")}
            </Button>
          )}
          <Button onClick={onClose} data-testid="tpl-analysis-close-button" className="rounded-md">
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
